'use client';

/* eslint-disable @typescript-eslint/no-explicit-any */
import React, {
	createContext,
	use,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from 'react';
import { usePathname } from 'next/navigation';

import { debounce } from 'codekit';
import trackter from 'trackter';
import { UAParser } from 'ua-parser-js';

import { UserRole } from 'config/auth';
import { encrypt } from 'helpers/crypto';
import { key } from 'helpers/key';
import { useVisitorId } from 'hooks/useVisitorId';
import {
	AnalyticsDevice,
	AnalyticsDeviceType,
	AnalyticsView,
	AnalyticsViewApiRequest,
} from 'models/Analytics';
import { api } from 'services/api';
import { useAuth } from './AuthContext';

interface AnalyticsContextProps {
	children: React.ReactNode;

	role: Promise<UserRole>;
}

type View = Pick<AnalyticsView, 'id' | 'pages'>;

export interface AnalyticsContextData {
	view: View | null;
	isLoading: boolean;
}

const CACHE_STORAGE_KEY = key('analytics/view');
let INTERACTION_TIMEOUT: NodeJS.Timeout;

export const AnalyticsContext = createContext({} as AnalyticsContextData);

export function AnalyticsContextProvider(props: AnalyticsContextProps) {
	// Hooks
	const auth = useAuth();
	const role = use(props.role);
	const visitorId = useVisitorId();
	const pathname = usePathname();

	// States
	const [view, setView] = useState<View | null>(null);
	const [isLoading, setIsLoading] = useState(false);

	// Memo vars
	const isValid = useMemo(() => {
		return (
			role !== 'admin' &&
			!visitorId.isLoading &&
			!auth.isLoading &&
			process.env.NODE_ENV !== 'development'
		);
	}, [auth.isLoading, role, visitorId.isLoading]);

	// Callbacks
	const getDevice = useCallback(async () => {
		const { data, hash, bot } = await trackter.identify({
			exclude: ['availableScreenResolution', 'screenResolution'],
		});

		const [width, height] = [data.screenResolution].flat().map(Number);
		const userAgent = String([data.userAgent].flat()[0]);

		let type: AnalyticsDeviceType = 'other';

		if (width < 768) type = 'mobile';
		else if (width < 1024) type = 'tablet';
		else if (width < 1440) type = 'laptop';
		else type = 'desktop';

		const isBot = bot.isBot
			? bot.botType
			: userAgent.includes('vercel-screenshot')
				? 'Vercel Screenshot'
				: false;

		const ua = new UAParser(userAgent).getResult();

		const device: AnalyticsDevice = {
			hash,
			type,
			bot: isBot,
			userAgent,

			resolution: { width, height },
			browser: ua.browser.name ?? null,
			os: ua.os.name ?? null,
		};

		return device;
	}, []);

	const getView = useCallback(async (): Promise<View> => {
		const cache = sessionStorage.getItem(CACHE_STORAGE_KEY);
		const cacheView = cache ? (JSON.parse(cache) as AnalyticsView) : null;
		if (cacheView) return { id: cacheView.id, pages: cacheView.pages };

		const [device] = await Promise.all([
			(() => getDevice().catch(() => null))(),
		]);

		const view: AnalyticsViewApiRequest = {
			pages: [],

			device,

			referrer: window.document.referrer || null,
			userId: auth.user?.uid ?? null,
			visitorId: visitorId.data ?? null,

			lastInteractionAt: Date.now(),
		};

		const response = await api.post<{ viewId: string }>('/analytics/view', {
			data: encrypt(JSON.stringify(view)),
		});

		return {
			pages: [],
			id: response.data.viewId,
		};
	}, [auth.user?.uid, getDevice, visitorId.data]);

	const logView = useCallback(async () => {
		if (!isValid) return;

		const view = await getView();

		const page = {
			href: window.location.href,
			date: new Date().toISOString(),
		};

		view.pages.push(page);

		await api.put(`/analytics/view/${view.id}`, {
			page,
			userId: auth.user?.uid ?? null,
		});

		setView(view);
		setIsLoading(false);
		sessionStorage.setItem(CACHE_STORAGE_KEY, JSON.stringify(view));
	}, [auth.user?.uid, getView, isValid]);

	// Effects
	useEffect(() => {
		if (!isValid) return;

		const handler = debounce(logView, 100);

		handler();
	}, [logView, isValid, pathname]);

	useEffect(() => {
		if (!isValid) return;
		if (!view) return;

		const handler = () => {
			clearTimeout(INTERACTION_TIMEOUT);

			const lastInteractionAt = Date.now();

			INTERACTION_TIMEOUT = setTimeout(async () => {
				await api.put(`/analytics/view/${view.id}`, {
					lastInteractionAt,
				});
			}, 3000);
		};

		window.addEventListener('mousemove', handler);
		window.addEventListener('keydown', handler);
		window.addEventListener('touchstart', handler);
		window.addEventListener('touchend', handler);
		window.addEventListener('scroll', handler);

		return () => {
			clearTimeout(INTERACTION_TIMEOUT);
			window.removeEventListener('mousemove', handler);
			window.removeEventListener('keydown', handler);
			window.removeEventListener('touchstart', handler);
			window.removeEventListener('touchend', handler);
			window.removeEventListener('scroll', handler);
		};
	}, [isValid, view]);

	return (
		<AnalyticsContext.Provider value={{ view, isLoading }}>
			{props.children}
		</AnalyticsContext.Provider>
	);
}

export const useAnalytics = () => useContext(AnalyticsContext);
